import { AxiosError, AxiosResponse } from 'axios';
import { computed, observable, reaction } from 'mobx';
import React from 'react';

import { API } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import { parseTextParams } from '../util/TextUtil';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';

export interface TranslationParameterMapping {
  [value: string]: any;
}

class TranslateStore {
  public _translationsPromise: Promise<void> = Promise.resolve(); // expose to test only

  @observable accessor fetched: boolean = false;
  @observable accessor translations: TranslationParameterMapping = {};

  private defaultTranslateAppName = 'ssoui';
  private globalTranslateAppName = 'global';
  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.fetchTranslations();
    reaction(
      () => this.languageStore.currentLanguage,
      () => this.fetchTranslations(),
    );
  }

  @computed
  public get currentTranslations() {
    return this.translations;
  }

  @computed
  public get globalTranslations() {
    return this.translations[this.globalTranslateAppName];
  }

  private fetchTranslations() {
    this._translationsPromise = this._translationsPromise
      .then(() => API.get(`/translations/${this.languageStore.currentLanguage}`))
      .then((response: AxiosResponse) => {
        this.translations = response.data.translations || {};
        this.fetched = true;
      })
      .catch((e: AxiosError) => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
      });
  }

  public translate = (key: string, paramObject?: TranslationParameterMapping) => {
    const value = this.getTranslationForGroup(this.defaultTranslateAppName, key, paramObject);
    const htmlTagRegexp = new RegExp('<\\/?((?!(iframe)\\b)\\w*)\\/?>');
    if (htmlTagRegexp.test(value)) {
      return this.translateHtml(value);
    }
    return value;
  };

  public translateHtml = (value: string) => {
    return <span dangerouslySetInnerHTML={{ __html: value }} />;
  };

  private getTranslationForGroup = (group: string, key: any, paramObject?: TranslationParameterMapping) => {
    const groupTranslations = this.translations[group];
    const label = `${group}:${key}`;
    let textValue;

    if (groupTranslations) {
      textValue = groupTranslations[key];
    }

    if (textValue) {
      if (paramObject) {
        return parseTextParams(textValue, paramObject);
      }
      return textValue;
    }

    const error: Error = new Error('Translation missing');
    error.stack = `label: ${label} , page: ${window.location.pathname}`;
    ErrorUtil.pushErrorToTeliaRumAndLog(error);
    return label;
  };
}

export default TranslateStore;
